import { Select, TextInput } from "flowbite-react";
import { ModalShell } from "../atoms/ModalShell";
import { FieldLabel } from "../atoms/FieldLabel";
import { Cap, Level } from "../../utils/rbacCatalog";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiOutlineDocumentText,
  HiOutlineTrash,
} from "react-icons/hi";
import { CustomButton } from "../atoms/CustomButton";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { PlanTier, usePermissions } from "../../utils/permissions";
import useAuth from "../../hooks/useAuth";
import UpgradeModal from "../modals/UpgradeModal";
import { PremiumBenefit } from "../atoms/UpgradePromptContent";
import FileDropzone from "../atoms/FileDropzone";
import HttpService from "../../services/http";
import { usersCache } from "../../hooks/users";
import { queryClient } from "../../providers/QueryProvider";
import { toast } from "react-toastify";
import { entityCache } from "../../hooks/entity";

type ImportFromDocumentProps = {
  userIdentification: string;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
};

const DOC_FIELD_LIMITS = {
  NAME: 60,
  DESCRIPTION: 300,
};

type Field = { name: string; description: string };
type DocTemplate = { id: string; name: string; fields: Field[] };

const PROFILE_FIELD_KEYS = new Set([
  "first_name",
  "last_name",
  "user_name",
  "country",
  "phone",
  "birth_date",
]);

type ImportPhase = "idle" | "uploading" | "analyzing" | "saving" | "error";

type ImportErrorReason =
  | "timeout"
  | "ocrFailed"
  | "unreadable"
  | "serviceUnavailable"
  | "generic";

const POLL_INTERVAL_MS = 8000;
const POLL_TIMEOUT_MS = 7 * 60 * 1000;
const SEND_PROGRESS_END = 25;

const IMPORT_STEPS: { phase: ImportPhase; key: string }[] = [
  { phase: "uploading", key: "sent" },
  { phase: "analyzing", key: "analyzing" },
  { phase: "saving", key: "saving" },
];

type StepState = "done" | "active" | "todo";

const stepState = (stepPhase: ImportPhase, current: ImportPhase): StepState => {
  const order: ImportPhase[] = ["uploading", "analyzing", "saving"];
  const si = order.indexOf(stepPhase);
  const ci = order.indexOf(current);
  if (ci < 0) return "todo";
  if (si < ci) return "done";
  if (si === ci) return "active";
  return "todo";
};

const StepIcon = ({ state }: { state: StepState }) => {
  if (state === "done")
    return <HiCheckCircle size={18} className="text-green-500 shrink-0" />;
  if (state === "active")
    return (
      <span className="h-[18px] w-[18px] shrink-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
    );
  return (
    <span className="h-[18px] w-[18px] shrink-0 rounded-full border-2 border-gray-300" />
  );
};

const DEFAULT_FIELDS: Field[] = [
  { name: "first_name", description: "Person's first or given name" },
  { name: "last_name", description: "Person's family or last name" },
  {
    name: "user_name",
    description: "Username or nickname used to identify the person",
  },
  {
    name: "country",
    description:
      "Country of residence or nationality (return as ISO country code, e.g. US, FR)",
  },
  {
    name: "phone",
    description:
      "Phone number including international country code (e.g. +1 555 000 0000)",
  },
  {
    name: "birth_date",
    description: "Date of birth, return in YYYY-MM-DD format",
  },
];

const ImportFromDocument = ({
  userIdentification,
  showModal,
  setShowModal,
}: ImportFromDocumentProps) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const [showImageUploadUpgradeModal, setShowImageUploadUpgradeModal] =
    useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [fields, setFields] = useState<Field[]>(DEFAULT_FIELDS);
  const [disabledProfileFields, setDisabledProfileFields] = useState<
    Set<string>
  >(new Set(PROFILE_FIELD_KEYS));
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [phase, setPhase] = useState<ImportPhase>("idle");
  const [errorReason, setErrorReason] = useState<ImportErrorReason | null>(
    null
  );
  const [progress, setProgress] = useState(0);
  const [fileRequired, setFileRequired] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [alsoEmbed, setAlsoEmbed] = useState(false);
  const [templates, setTemplates] = useState<DocTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  const SUBMIT_TIMEOUT_MS = 90 * 1000;

  const isBusy =
    phase === "uploading" || phase === "analyzing" || phase === "saving";

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  const SAVING_STEP_MS = 700;

  const entityIdentification = user?.entity_identification;

  useEffect(() => {
    if (!showModal || !entityIdentification) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await HttpService.get<{
          element: { templates: DocTemplate[] };
        }>(
          `${customer_identification}/${entityIdentification}/user/ai-doc-templates`
        );
        if (!cancelled) setTemplates(res.data?.element?.templates ?? []);
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showModal, entityIdentification, customer_identification]);

  const handleSelectTemplate = (id: string) => {
    setSelectedTemplateId(id);
    if (!id) {
      setFields(DEFAULT_FIELDS);
      return;
    }
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    const merged: Field[] = [...DEFAULT_FIELDS];
    const seen = new Set(merged.map((f) => f.name));
    tpl.fields.forEach((f) => {
      if (f.name && !seen.has(f.name)) {
        merged.push({ name: f.name, description: f.description || "" });
        seen.add(f.name);
      }
    });
    setFields(merged);
  };

  const handleAddField = () => {
    const name = newName.trim().replace(/\s+/g, "_");
    if (!name || fields.some((f) => f.name === name)) return;
    setFields((prev) => [
      ...prev,
      { name, description: newDescription.trim() },
    ]);
    setNewName("");
    setNewDescription("");
  };

  const pendingField = (): Field | null => {
    const name = newName.trim().replace(/\s+/g, "_");
    if (!name || fields.some((f) => f.name === name)) return null;
    return { name, description: newDescription.trim() };
  };

  const handleDeleteField = (name: string) => {
    setFields((prev) => prev.filter((f) => f.name !== name));
  };

  const handleDescriptionChange = (name: string, description: string) => {
    setFields((prev) =>
      prev.map((f) => (f.name === name ? { ...f, description } : f))
    );
  };

  const toggleProfileField = (name: string) => {
    setDisabledProfileFields((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const profileFieldNames = fields
    .filter((f) => PROFILE_FIELD_KEYS.has(f.name))
    .map((f) => f.name);
  const checkedProfileCount = profileFieldNames.filter(
    (n) => !disabledProfileFields.has(n)
  ).length;
  const allProfileChecked =
    profileFieldNames.length > 0 &&
    checkedProfileCount === profileFieldNames.length;
  const someProfileChecked = checkedProfileCount > 0 && !allProfileChecked;

  const toggleAllProfileFields = () => {
    setDisabledProfileFields(
      allProfileChecked ? new Set(profileFieldNames) : new Set()
    );
  };

  const masterCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (masterCheckboxRef.current)
      masterCheckboxRef.current.indeterminate = someProfileChecked;
  }, [someProfileChecked]);

  const invalidateCaches = useCallback(() => {
    usersCache.delete(`user_profile_${userIdentification}`);
    queryClient.invalidateQueries("profile");
    queryClient.invalidateQueries(["entity_user", userIdentification]);

    usersCache.delete(`user_preferences_all_${userIdentification}`);
    queryClient.invalidateQueries(["user-preferences", userIdentification]);

    usersCache.delete(`user_data_${userIdentification}`);
    entityCache.delete(
      `user_data_${customer_identification}_${entityIdentification}_${userIdentification}`
    );
    queryClient.invalidateQueries([
      "user-data",
      customer_identification,
      entityIdentification,
      userIdentification,
    ]);

    entityCache.delete(
      `user_profile_data_${customer_identification}_${entityIdentification}_${userIdentification}`
    );
    queryClient.invalidateQueries(["user-profile-data"], { exact: false });
  }, [userIdentification, customer_identification, entityIdentification]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    pollTimerRef.current = null;
    progressTimerRef.current = null;
    closeTimerRef.current = null;
  }, []);

  const resetAndClose = useCallback(() => {
    cancelledRef.current = true;
    stopPolling();
    setAttachmentFile(null);
    setFields(DEFAULT_FIELDS);
    setNewName("");
    setNewDescription("");
    setFileRequired(false);
    setFileSizeError(false);
    setAlsoEmbed(false);
    setDisabledProfileFields(new Set(PROFILE_FIELD_KEYS));
    setSelectedTemplateId("");
    setPhase("idle");
    setErrorReason(null);
    setProgress(0);
    setShowModal(false);
  }, [stopPolling, setShowModal]);

  const failWith = useCallback(
    (reason: ImportErrorReason) => {
      stopPolling();
      setErrorReason(reason);
      setPhase("error");
    },
    [stopPolling]
  );

  const buildFieldsObj = () => {
    const pending = pendingField();
    const allFields = pending ? [...fields, pending] : fields;
    return allFields
      .filter((f) => !disabledProfileFields.has(f.name))
      .map(({ name, description }) => ({ name, description }));
  };

  const pollStatus = useCallback(
    (
      docId: string,
      cdnFile: { file_name: string; extension: string } | null,
      fieldsObj: Field[],
      startedAt: number
    ) => {
      const tick = async () => {
        if (cancelledRef.current) return;
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          failWith("timeout");
          return;
        }
        try {
          const params = new URLSearchParams({
            fields: JSON.stringify(fieldsObj),
            also_embed: String(alsoEmbed),
          });
          if (cdnFile) {
            params.set("cdn_file_name", cdnFile.file_name);
            params.set("extension", cdnFile.extension ?? "");
          }
          const res = await HttpService.get<{
            element: { status: string; result?: any; reason?: string };
          }>(
            `${customer_identification}/${entityIdentification}/user/${userIdentification}/import-from-document/status/${docId}?${params.toString()}`
          );
          if (cancelledRef.current) return;

          const status = res?.data?.element?.status;
          if (status === "success") {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
            if (progressTimerRef.current)
              clearInterval(progressTimerRef.current);
            pollTimerRef.current = null;
            progressTimerRef.current = null;
            setProgress(100);
            setPhase("saving");
            invalidateCaches();
            toast.success(t("dashboard.importFromDocument.successToast"));
            closeTimerRef.current = setTimeout(resetAndClose, SAVING_STEP_MS);
            return;
          }
          if (status === "error") {
            failWith("ocrFailed");
            return;
          }
          pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        } catch (error: any) {
          if (cancelledRef.current) return;
          const status = error?.response?.status;
          const isTerminal =
            typeof status === "number" &&
            status >= 400 &&
            status < 500 &&
            status !== 429;
          if (isTerminal) {
            failWith("serviceUnavailable");
          } else if (Date.now() - startedAt <= POLL_TIMEOUT_MS) {
            pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
          } else {
            failWith("serviceUnavailable");
          }
        }
      };
      tick();
    },
    [
      alsoEmbed,
      customer_identification,
      entityIdentification,
      userIdentification,
      invalidateCaches,
      resetAndClose,
      failWith,
      t,
      SAVING_STEP_MS,
    ]
  );

  const handleConfirm = async () => {
    if (isBusy) return;
    if (!permissions.can(Cap.READ_A_DOCUMENT, Level.WRITE)) return;
    if (!permissions.hasFeatureAccess("IMPORT_FROM_DOCUMENT")) {
      setShowImageUploadUpgradeModal(true);
      return;
    }
    if (!attachmentFile) {
      setFileRequired(true);
      return;
    }
    if (attachmentFile.size > MAX_FILE_SIZE) {
      setFileSizeError(true);
      return;
    }
    const fieldsObj = buildFieldsObj();
    if (fieldsObj.length === 0 && !alsoEmbed) {
      toast.error(t("dashboard.importFromDocument.nothingToDoError"));
      return;
    }

    stopPolling();
    cancelledRef.current = false;
    setErrorReason(null);
    setProgress(SEND_PROGRESS_END / 2);
    setPhase("uploading");

    try {
      const form = new FormData();
      form.append("file", attachmentFile, attachmentFile.name);
      form.append("fields", JSON.stringify(fieldsObj));
      form.append("also_embed", String(alsoEmbed));

      const res = await HttpService.post<{
        element: {
          doc_id: string;
          cdn_file: { file_name: string; extension: string } | null;
        };
      }>(
        `${customer_identification}/${entityIdentification}/user/${userIdentification}/import-from-document`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: SUBMIT_TIMEOUT_MS,
        }
      );
      if (cancelledRef.current) return;

      const docId = res?.data?.element?.doc_id;
      const cdnFile = res?.data?.element?.cdn_file ?? null;
      if (!docId) {
        failWith("serviceUnavailable");
        return;
      }

      setProgress(SEND_PROGRESS_END);

      setPhase("analyzing");
      const startedAt = Date.now();
      progressTimerRef.current = setInterval(() => {
        const analysisPct = Math.min(
          1,
          (Date.now() - startedAt) / POLL_TIMEOUT_MS
        );
        setProgress(
          SEND_PROGRESS_END + analysisPct * (100 - SEND_PROGRESS_END)
        );
      }, 1000);
      pollStatus(docId, cdnFile, fieldsObj, startedAt);
    } catch (error: any) {
      if (cancelledRef.current) return;
      const status = error?.response?.status;
      failWith(
        status === 400
          ? "unreadable"
          : status === 429 || status === 503
          ? "serviceUnavailable"
          : "generic"
      );
    }
  };

  const handleClose = () => {
    if (isBusy) return;
    resetAndClose();
  };

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      stopPolling();
    };
  }, [stopPolling]);

  const documentReadingBenefits: PremiumBenefit[] = [
    {
      id: "doc-reading-extract",
      text: t("dashboard.upgrade.documentReading.benefit1") as string,
    },
    {
      id: "doc-reading-autofill",
      text: t("dashboard.upgrade.documentReading.benefit2") as string,
    },
    {
      id: "doc-reading-no-retype",
      text: t("dashboard.upgrade.documentReading.benefit3") as string,
    },
  ];

  return (
    <>
      <ModalShell
        show={showModal}
        onClose={() => handleClose()}
        dismissible={!isBusy}
        size="2xl"
        title={t("dashboard.importFromDocument.title")}
        bodyClassName="p-0"
        footer={
          <>
            <CustomButton
              onClick={() => handleClose()}
              title={t("common.cancel") as string}
              disabled={isBusy}
              variant="secondary"
            />

            <CustomButton
              variant={"primary"}
              onClick={handleConfirm}
              title={
                phase === "error"
                  ? (t("dashboard.importFromDocument.retry") as string)
                  : (t("dashboard.importFromDocument.confirm") as string)
              }
              disabled={
                isBusy || !permissions.can(Cap.READ_A_DOCUMENT, Level.WRITE)
              }
              isLoading={isBusy}
            />
          </>
        }
      >
        <div className="p-6 flex flex-col gap-4">
          <div
            onClick={() => {
              if (
                !attachmentFile &&
                !permissions.hasFeatureAccess("IMPORT_FROM_DOCUMENT")
              ) {
                setShowImageUploadUpgradeModal(true);
              }
            }}
          >
            <FileDropzone
              selectedFile={attachmentFile}
              onFileSelected={(file) => {
                setAttachmentFile(file);
                setFileRequired(false);
                setFileSizeError(file.size > MAX_FILE_SIZE);
              }}
              onFileRemoved={() => {
                setAttachmentFile(null);
                setFileSizeError(false);
              }}
              accept={{
                "application/pdf": [".pdf"],
                "image/png": [".png"],
                "image/jpeg": [".jpg", ".jpeg"],
                "image/webp": [".webp"],
                "text/plain": [".txt"],
                "text/markdown": [".md"],
                "application/json": [".json"],
              }}
              hint={t("dashboard.importFromDocument.hint") as string}
              disabled={!permissions.hasFeatureAccess("IMPORT_FROM_DOCUMENT")}
              hasError={(fileRequired && !attachmentFile) || fileSizeError}
              errorMessage={
                fileSizeError
                  ? (t("dashboard.importFromDocument.fileSizeError") as string)
                  : (t("dashboard.importFromDocument.fileRequired") as string)
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <FieldLabel className="mb-0">
              {t("dashboard.importFromDocument.documentType.label")}
            </FieldLabel>
            <Select
              value={selectedTemplateId}
              onChange={(e) => handleSelectTemplate(e.target.value)}
            >
              <option value="">
                {t("dashboard.importFromDocument.documentType.custom")}
              </option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </Select>
            {templates.length > 0 && (
              <span className="text-xs text-gray-500">
                {t("dashboard.importFromDocument.documentType.hint")}
              </span>
            )}
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={alsoEmbed}
              onChange={(e) => setAlsoEmbed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-gray-900">
                {t("dashboard.importFromDocument.alsoEmbed.label")}
              </span>
              <span className="text-xs text-gray-500">
                {t("dashboard.importFromDocument.alsoEmbed.description")}
              </span>
            </span>
          </label>

          <div className="flex flex-col gap-4">
            <div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">
                  {t("dashboard.importFromDocument.profileFields.title")}
                </h4>
                <span className="text-xs text-gray-500">
                  {t("dashboard.importFromDocument.profileFields.subtitle")}
                </span>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                  <input
                    ref={masterCheckboxRef}
                    type="checkbox"
                    checked={allProfileChecked}
                    onChange={toggleAllProfileFields}
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs font-medium text-gray-700">
                    {t("dashboard.importFromDocument.profileFields.selectAll")}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 px-4 py-3">
                  {fields
                    .filter((f) => PROFILE_FIELD_KEYS.has(f.name))
                    .map(({ name }) => {
                      const enabled = !disabledProfileFields.has(name);
                      return (
                        <label
                          key={name}
                          className="flex items-center gap-2 cursor-pointer select-none py-1"
                        >
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => toggleProfileField(name)}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span
                            className={`text-sm ${
                              enabled ? "text-gray-800" : "text-gray-400"
                            }`}
                          >
                            {t(
                              `dashboard.importFromDocument.profileFields.labels.${name}`
                            )}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </div>
            </div>

            <div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">
                  {t("dashboard.importFromDocument.userDetails.title")}
                </h4>
                <span className="text-xs text-gray-500">
                  {t("dashboard.importFromDocument.userDetails.subtitle")}
                </span>
              </div>
              <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
                {fields
                  .filter((f) => !PROFILE_FIELD_KEYS.has(f.name))
                  .map(({ name, description }) => (
                    <li
                      key={name}
                      className="grid grid-cols-[1fr_auto] sm:flex sm:items-center gap-x-2 gap-y-1 px-4 py-2 hover:bg-gray-50"
                    >
                      <TextInput
                        type="text"
                        sizing="sm"
                        value={name}
                        readOnly
                        title={name}
                        className="w-full sm:w-32 sm:shrink-0 min-w-0"
                        theme={{
                          field: {
                            input: {
                              withShadow: {
                                off: "font-mono bg-gray-100 text-gray-500 truncate cursor-default",
                              },
                            },
                          },
                        }}
                      />
                      <CustomButton
                        type="button"
                        variant="none"
                        size="icon"
                        onClick={() => handleDeleteField(name)}
                        className="row-span-2 self-center justify-self-end sm:row-auto sm:order-last shrink-0 rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                        icon={<HiOutlineTrash size={18} />}
                      />
                      <TextInput
                        type="text"
                        sizing="sm"
                        value={description}
                        onChange={(e) =>
                          handleDescriptionChange(name, e.target.value)
                        }
                        placeholder={
                          t(
                            "dashboard.importFromDocument.userDetails.placeholder"
                          ) as string
                        }
                        maxLength={DOC_FIELD_LIMITS.DESCRIPTION}
                        className="flex-1 min-w-0"
                      />
                    </li>
                  ))}
                <li className="px-4 py-3 bg-gray-50 flex flex-col gap-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <TextInput
                      type="text"
                      sizing="sm"
                      value={newName}
                      onChange={(e) =>
                        setNewName(e.target.value.replace(/\s+/g, "_"))
                      }
                      placeholder={
                        t(
                          "dashboard.importFromDocument.userDetails.fieldNamePlaceholder"
                        ) as string
                      }
                      maxLength={DOC_FIELD_LIMITS.NAME}
                      className="w-full sm:w-32 sm:shrink-0"
                    />
                    <TextInput
                      type="text"
                      sizing="sm"
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddField()}
                      placeholder={
                        t(
                          "dashboard.importFromDocument.userDetails.fieldDescriptionPlaceholder"
                        ) as string
                      }
                      maxLength={DOC_FIELD_LIMITS.DESCRIPTION}
                      className="flex-1 min-w-0"
                    />
                  </div>
                  <div className="flex justify-end">
                    <CustomButton
                      variant="primary"
                      icon="plus"
                      title={t("common.add") as string}
                      onClick={handleAddField}
                      disabled={!newName.trim()}
                      size="sm"
                    />
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {isBusy && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col gap-3">
            <ol className="flex flex-col gap-2.5">
              {IMPORT_STEPS.map((step) => {
                const state = stepState(step.phase, phase);
                return (
                  <li
                    key={step.phase}
                    className="flex items-center gap-2.5 text-sm"
                  >
                    <StepIcon state={state} />
                    <span
                      className={
                        state === "done"
                          ? "text-gray-500"
                          : state === "active"
                          ? "text-gray-900 font-medium"
                          : "text-gray-400"
                      }
                    >
                      {t(`dashboard.importFromDocument.steps.${step.key}`)}
                    </span>
                  </li>
                );
              })}
            </ol>

            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
              role="progressbar"
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className="h-full rounded-full bg-blue-500 transition-[width] duration-1000 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>

            <p className="text-xs text-gray-500">
              {t("dashboard.importFromDocument.status.timeoutHint")}
            </p>
          </div>
        )}

        {phase === "error" && (
          <div className="px-6 py-4 bg-red-50 border-t border-red-100 flex items-start gap-3">
            <HiExclamationCircle
              size={20}
              className="text-red-500 shrink-0 mt-0.5"
            />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-red-800">
                {t("dashboard.importFromDocument.errors.title")}
              </span>
              <span className="text-sm text-red-700">
                {t(
                  `dashboard.importFromDocument.errors.${
                    errorReason ?? "generic"
                  }`
                )}
              </span>
            </div>
          </div>
        )}
      </ModalShell>

      <UpgradeModal
        showModal={showImageUploadUpgradeModal}
        setShowModal={setShowImageUploadUpgradeModal}
        targetPlan={PlanTier.ENTERPRISE}
        title={t("dashboard.upgrade.documentReading.title") as string}
        description={
          t("dashboard.upgrade.documentReading.description") as string
        }
        benefits={documentReadingBenefits}
        icon={HiOutlineDocumentText}
        iconTheme="blue"
        upgradeText={t("dashboard.upgrade.key3") as string}
      />
    </>
  );
};

export default ImportFromDocument;
